import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useSession } from "../shell/session";
import { Loading } from "../ui";

/** Where a break-glass link lands (ADR-0173; #1012). `zenith-api breakglass`
 * prints `https://<host>/auth/break-glass#<token>`: the token is the
 * **fragment**, which a browser never sends to a server, so it reaches Zenith
 * only in the body this page posts. */
export const BREAK_GLASS_PATH = "/auth/break-glass";

/** Restates ADR-0173 decision 1a — the one refusal, naming nothing (§6.3): a
 * spent, expired or unknown link, and one for a user no longer a superuser,
 * are refused alike. */
export const BREAK_GLASS_REFUSED = "That break-glass link cannot be used. It may have been used already, or expired. Ask whoever holds the server to issue another.";

/** The link carried no fragment: somebody cut it short, or opened the page by
 * its address. Said apart because the person can act on it. */
export const BREAK_GLASS_NO_TOKEN = "This link is missing its token. Open the whole link exactly as it was printed.";

/** The fragment, read once, before anything else can see the address. */
function fragment(): string {
  return window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
}

/** The spend. It posts the fragment once and takes it out of the address bar
 * and the history entry at the same moment, so the token is not left behind
 * for the next person at this browser — the session cookie is what carries the
 * sign-in from here. */
export default function BreakGlass() {
  const { refresh } = useSession();
  const navigate = useNavigate();
  // Read in the initializer, before the effect below strips it.
  const [token] = useState(fragment);
  const [refused, setRefused] = useState(false);
  // Once per page, whatever React does with effects in development: a second
  // post would find the link spent and answer the refusal.
  const sent = useRef(false);

  useEffect(() => {
    window.history.replaceState(window.history.state, "", BREAK_GLASS_PATH);
    if (!token || sent.current) return;
    sent.current = true;
    void (async () => {
      const { response } = await api.POST("/auth/break-glass", { body: { token } });
      if (!response.ok) { setRefused(true); return; }
      await refresh();
      navigate("/", { replace: true });
    })();
  }, [token, refresh, navigate]);

  if (token && !refused) return <main className="gate"><Loading label="Letting you in" /></main>;
  return (
    <main className="gate">
      <div className="gate-card form">
        <div>
          <h1>Break-glass sign-in</h1>
          <p className="muted">A break-glass link lets a superuser in while the identity provider is failing.</p>
        </div>
        <div role="alert" className="problem"><div><strong>{token ? BREAK_GLASS_REFUSED : BREAK_GLASS_NO_TOKEN}</strong></div></div>
      </div>
    </main>
  );
}

/** Restates ADR-0173 decision 4a — the banner every page of a break-glass
 * session carries, drawn from `GET /v1/me`'s `break_glass`. */
export const BREAK_GLASS_BANNER = "Break-glass session. You are signed in as a superuser without the identity provider, with no organization's role. Every change you make is audited as break-glass, and every other superuser has been told.";

export function BreakGlassBanner() {
  return <div className="break-glass-banner" role="status">{BREAK_GLASS_BANNER}</div>;
}
